import React, { useEffect, useState } from "react";
import ConfirmActionModal from "components/common/ConfirmActionModal";
import type { QuotationWorkflow } from "features/Quotations/hooks/useQuotationWorkflow";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";

type Props = {
  workflow: QuotationWorkflow;
};

export default function QuotationWorkflowModals({ workflow }: Props) {
  const {
    workflowBusy,
    activeQuotation,
    sendConfirmOpen,
    setSendConfirmOpen,
    resendConfirmOpen,
    setResendConfirmOpen,
    acceptModalOpen,
    setAcceptModalOpen,
    cancelModalOpen,
    setCancelModalOpen,
    rejectModalOpen,
    setRejectModalOpen,
    runSend,
    runResend,
    runAccept,
    runCancel,
    runReject,
  } = workflow;

  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    if (rejectModalOpen) setRejectReason("");
  }, [rejectModalOpen]);

  return (
    <React.Fragment>
      <ConfirmActionModal
        show={sendConfirmOpen}
        onClose={() => !workflowBusy && setSendConfirmOpen(false)}
        title="Send quotation"
        iconClass="ri-send-plane-line"
        iconColor="text-primary"
        heading="Send to customer?"
        description={
          <>
            <p className="mb-2">
              This emails the quotation PDF to the client&apos;s address on
              file. The status changes from <strong>Draft</strong> to{" "}
              <strong>Sent</strong>.
            </p>
            <p className="mb-0 small text-muted">
              Ensure the client record has a valid email before sending.
            </p>
          </>
        }
        confirmText={workflowBusy ? "Sending…" : "Send"}
        confirmColor="primary"
        confirmDisabled={workflowBusy}
        onConfirm={() => void runSend()}
      />

      <ConfirmActionModal
        show={resendConfirmOpen}
        onClose={() => !workflowBusy && setResendConfirmOpen(false)}
        title="Resend quotation"
        iconClass="ri-mail-send-line"
        iconColor="text-primary"
        heading="Resend to customer?"
        description={
          <>
            <p className="mb-2">
              This re-emails the quotation PDF to the client&apos;s address on
              file.
            </p>
            <p className="mb-0 small text-muted">
              Quotation status remains unchanged.
            </p>
          </>
        }
        confirmText={workflowBusy ? "Resending…" : "Resend"}
        confirmColor="primary"
        confirmDisabled={workflowBusy}
        onConfirm={() => void runResend()}
      />

      <ConfirmActionModal
        show={acceptModalOpen}
        onClose={() => !workflowBusy && setAcceptModalOpen(false)}
        title="Accept quotation"
        iconClass="ri-check-double-line"
        iconColor="text-success"
        heading="Accept this quotation?"
        description={
          <>
            <p className="mb-2">
              This confirms customer approval and moves the quotation to{" "}
              <strong>Accepted</strong>.
            </p>
            <p className="mb-0 small text-muted">
              {activeQuotation?.code
                ? `Quotation: ${activeQuotation.code}`
                : "You can continue with downstream actions after acceptance."}
            </p>
          </>
        }
        confirmText={workflowBusy ? "Accepting…" : "Accept"}
        confirmColor="success"
        confirmDisabled={workflowBusy}
        onConfirm={() => void runAccept()}
      />

      <ConfirmActionModal
        show={cancelModalOpen}
        onClose={() => !workflowBusy && setCancelModalOpen(false)}
        title="Cancel quotation"
        iconClass="ri-close-circle-line"
        iconColor="text-danger"
        heading="Cancel this quotation?"
        description={
          <>
            <p className="mb-2">
              This marks the quotation as <strong>Cancelled</strong> and stops
              further workflow actions.
            </p>
            <p className="mb-0 small text-muted">
              This action cannot be undone from this screen.
            </p>
          </>
        }
        confirmText={workflowBusy ? "Cancelling…" : "Cancel quotation"}
        confirmColor="danger"
        confirmDisabled={workflowBusy}
        onConfirm={() => void runCancel()}
      />

      <Modal
        isOpen={rejectModalOpen}
        toggle={() => !workflowBusy && setRejectModalOpen(false)}
        centered
      >
        <ModalHeader toggle={() => !workflowBusy && setRejectModalOpen(false)}>
          Reject quotation
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            This marks the quotation as <strong>Rejected</strong>. If it is
            linked to an enquiry that is <strong>Quoted</strong>, the enquiry
            returns to <strong>Inspection done</strong>.
          </p>
          {activeQuotation?.code ? (
            <p className="small text-muted mb-3">
              Quotation: {activeQuotation.code}
            </p>
          ) : null}
          <FormGroup>
            <Label for="quotation-reject-reason">Reason (optional)</Label>
            <Input
              id="quotation-reject-reason"
              type="textarea"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              disabled={workflowBusy}
              placeholder="Why was this quotation rejected?"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            onClick={() => !workflowBusy && setRejectModalOpen(false)}
          >
            Back
          </Button>
          <Button
            color="danger"
            disabled={workflowBusy}
            onClick={() =>
              void runReject(rejectReason.trim() || undefined)
            }
          >
            {workflowBusy ? "Rejecting…" : "Reject quotation"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
}
